import {
  Box,
  Button,
  DataTable,
  FlexBox,
  Icon,
  IconButton,
  Page,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { electron } from 'lib-shared-electron';
import { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';

import {
  jabraActions,
  JabraDevice,
  jabraDeviceManager,
} from 'middleware/jabra';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { isWebHidSupportedInBrowser } from '../../utils/isWebHidSupportedInBrowser';

export const DevicesSection = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const isWebHIDEnabled =
    !electron.isElectron() && isWebHidSupportedInBrowser();
  const isDeviceChangeSupported = isWebHIDEnabled;
  const [isSimulatingIncomingCall, setIsSimulatingIncomingCall] =
    useState(false);

  useEffect(() => {
    dispatch(jabraActions.jabraList());
  }, [dispatch]);

  const { devicesListState, devices } = useAppSelector(
    (state) => ({
      devicesListState: state.jabra.meta.list,
      devices: state.jabra.jabraList,
    }),
    shallowEqual,
  );

  const isDevicesLoading = devicesListState === 'pending';
  const isDevicesError = devicesListState === 'rejected';

  const openAddDevice = () => {
    dispatch(jabraActions.jabraRequestDevice());
  };

  const changeDevice = async (device: JabraDevice) => {
    try {
      await jabraDeviceManager.changeDeviceBySerialNumber(device.serialNumber);
    } catch (error) {
      console.error('Could not change the device', error);
    }
  };

  const testDeviceRing = () => {
    setIsSimulatingIncomingCall(true);
    dispatch(jabraActions.jabraRing());

    /** Turn off after 5s */
    setTimeout(() => {
      dispatch(jabraActions.jabraUnring());
      setIsSimulatingIncomingCall(false);
    }, 5000);
  };

  return (
    <Page.Section
      title="Devices"
      controls={
        isWebHIDEnabled && (
          <FlexBox gap="200">
            <Button variant="primary" onClick={() => openAddDevice()}>
              Add device
            </Button>
          </FlexBox>
        )
      }
    >
      <DataTable
        tableStatusProps={{
          loading: isDevicesLoading,
          loadingFailed: isDevicesError,
          emptyState:
            !isDevicesLoading && !isDevicesError && !devices.length
              ? {
                  title: 'No supported Jabra devices found',
                  description:
                    'Connect a Jabra device with USB and click Add device.',
                }
              : undefined,
        }}
        data={devices ?? []}
        columns={[
          {
            label: 'Device',
            selector: (item) => (
              <FlexBox gap="300" alignItems="center">
                {item.isLocked && (
                  <Icon
                    name="done"
                    color={typography.color['success']}
                    size={24}
                  />
                )}
                {!item.isLocked && <Box width="24px" />}
                <Box>{item.deviceName}</Box>
              </FlexBox>
            ),
          },
          {
            selector: (item) => (
              <FlexBox gap="200" justifyContent="end">
                {!item.isLocked && isDeviceChangeSupported && (
                  <Button
                    disabled={isSimulatingIncomingCall}
                    variant="secondary"
                    onClick={() => changeDevice(item)}
                  >
                    Set as active device
                  </Button>
                )}
                {item.isLocked && (
                  <>
                    <Tooltip
                      content="Simulate an incoming call for 5 seconds"
                      side="left"
                    >
                      <IconButton
                        isLoading={isSimulatingIncomingCall}
                        icon="phone"
                        variant="secondary"
                        onClick={() => testDeviceRing()}
                      />
                    </Tooltip>
                  </>
                )}
              </FlexBox>
            ),
          },
        ]}
      />
    </Page.Section>
  );
};
